import { Box, Flex } from '@chakra-ui/react';
import { ReactComponent as MoveIcon } from 'assets/icons/move.svg';
import { DRAG_HANDLE_CLASS, DRAG_HANDLE_ID_PREFIX } from 'constants/bounding-box';
import { useResizeObserver } from 'hooks/useResizeObserver';
import { useFlowCoreValue } from 'modules/Editor/components/FlowCore';
import { useAppSelector } from 'modules/Editor/store/hooks';
import { selectWidgetActionState } from 'modules/Editor/store/widgetSelector';
import { memo, useLayoutEffect } from 'react';
import { WidgetId } from 'types/idTypes';
import { WidgetType } from 'types/widget.types';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';

import { useEditor } from './useEditor';
import { useWidget } from './useWidget';

const CONTROL_TOP_DISTANCE = 12;
const CONTROL_OFFSET_FOR_FLOW_PORTS = 18;
const ICON_SIZE = 16;
const HANDLE_SIZE = 24;
const DRAG_HANDLE_BOX_ID = 'drag-handle-box';

type DragHandleConfig = { [key in WidgetType]?: boolean };

export const WIDGETS_WITH_DRAG_HANDLE: DragHandleConfig = {
  [WidgetType.Text]: true,
  [WidgetType.Table]: true,
};

export const hasDragHandle = (id: WidgetId) => {
  if (!id) return false;
  const widgetType = getWidgetTypeFromId(id);

  return !!WIDGETS_WITH_DRAG_HANDLE[widgetType];
};

/**
 * Extends the bounding box to include a drag handle
 * To include the drag handle for a widget - add it to the WIDGETS_WITH_DRAG_HANDLE config
 *
 */
export const DragHandle = memo(() => {
  const { isLocked } = useWidget(); // this has widgetId only. not target.
  const { isWidgetSelected, isGroupMember, isCropView } = useEditor();

  if (!isWidgetSelected) return null;
  if (isGroupMember) return null;
  if (isLocked) return null;
  if (isCropView) return null;

  return <DragHandleBox />;
});
DragHandle.displayName = 'DragHandle';

/**
 * Update the position of the drag handle, based on the height of the widget
 *
 * @param zoom - the editor zoom level
 * @param widgetEl
 * @returns
 */
const updatePosition = (zoom: number, widgetEl?: HTMLElement, marginTopPosition: number = 0) => {
  const widgetHeight = widgetEl?.clientHeight;
  const widgetWidth = widgetEl?.clientWidth;
  if (typeof widgetHeight !== 'number') return;
  if (typeof widgetWidth !== 'number') return;

  const dragHandleEl = document.querySelector(`.${DRAG_HANDLE_CLASS}`) as HTMLElement;
  const dragHandleBoxEl = document.getElementById(DRAG_HANDLE_BOX_ID) as HTMLElement;
  if (dragHandleEl && dragHandleBoxEl) {
    // increase the distance between the drag handle and the widget
    dragHandleEl.style.marginTop = `${widgetHeight + marginTopPosition / zoom}px`;
    // increase the height of the drag handle box to match the new widget height
    dragHandleBoxEl.style.height = `${widgetHeight}px`;
    // increase the width of the drag handle box to match the new widget width
    dragHandleBoxEl.style.width = `${widgetWidth}px`;
  }
};

const DragHandleBox = () => {
  const { widthPx, heightPx, topPx, leftPx, rotateDeg, widgetId } = useWidget();
  const { widgetRefs, zoom } = useEditor();
  const isActiveWidgetLinesConnected = useFlowCoreValue('isActiveWidgetLinesConnected');
  const widgetActionState = useAppSelector(selectWidgetActionState);

  // Render drag handler below the flow ports if there are connected widgets
  const marginTopPosition = isActiveWidgetLinesConnected
    ? CONTROL_OFFSET_FOR_FLOW_PORTS + CONTROL_TOP_DISTANCE
    : CONTROL_TOP_DISTANCE;

  const style = {
    width: `${widthPx}px`,
    height: `${heightPx}px`,
    top: `${topPx}px`,
    left: `${leftPx}px`,
    transform: `rotate(${rotateDeg}deg)`,
    // the drag handle should on be on top of all other widgets
    zIndex: 9999999,
  } as React.CSSProperties;

  // while an active widget is dragged/resized etc it should be hidden
  // visibility is used instead of rendering null to prevent layout shift when the widgetActionState is toggled quickly
  const visibility = widgetActionState ? 'hidden' : undefined;
  const widgetEl = widgetRefs[widgetId]?.element;

  useResizeObserver({
    ref: widgetRefs[widgetId]?.element,
    onResize: () => {
      updatePosition(zoom, widgetEl, marginTopPosition);
    },
  });

  // update the handle position once the zoom has changed
  useLayoutEffect(() => updatePosition(zoom, widgetEl, marginTopPosition), [zoom, widgetEl, marginTopPosition]);

  // scale the handle size based on the zoom level
  const handleSize = HANDLE_SIZE / zoom;
  const iconSize = ICON_SIZE / zoom;
  const shadowBlur = 4 / zoom;
  const shadowSpread = 1 / zoom;
  const dragHandleId = `${DRAG_HANDLE_ID_PREFIX}${widgetId}`;

  return (
    <Box
      visibility={visibility}
      id={DRAG_HANDLE_BOX_ID}
      pointerEvents='none'
      position='absolute'
      bg='transparent'
      style={style}
    >
      <Box w='100%' h='100%' position='relative'>
        <Flex
          cursor='move'
          id={dragHandleId}
          className={DRAG_HANDLE_CLASS}
          pointerEvents='auto'
          _hover={{ cursor: 'move', backgroundColor: 'gray.100' }}
          transition='background-color 0.1s linear'
          align='center'
          justify='center'
          borderRadius='50%'
          bg='white'
          boxShadow={`0 0 ${shadowBlur}px ${shadowSpread}px rgb(57 76 96 / 15%), 0 0 0 ${shadowSpread}px rgb(47 64 80 / 25%)`}
          width={`${handleSize}px`}
          h={`${handleSize}px`}
          mt={`${heightPx + marginTopPosition / zoom}px`}
          ml='50%'
        >
          <MoveIcon width={iconSize} height={`${iconSize}`} />
        </Flex>
      </Box>
    </Box>
  );
};
